import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';

import {
  Button,
  Chip,
  Container,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import {
  Criticality,
  FindingStatus,
  UsualFindings,
  saveFinding,
  selectSelectedFinding,
} from '../../redux/reducers/findingsReducer';
import DocumentLink from '../DocumentLink';

function validate(details) {
  return details.title !== '' && details.criticality !== '' && details.description !== '';
}

function FindingModal({ handleClose, controlId, description = '' }) {
  const initialFindingDetails = {
    controlId,
    title: '',
    criticality: '',
    description: '',
    evidence: [],
    findingStatus: FindingStatus.PENDING,
    resolved: false,
    notes: '',
    quickTags: [],
    recommendation: '',
    response: '',
    usualFindings: [],
  };
  const controlHeader = useSelector((state) => selectTitle(state, controlId));
  const finding = useSelector(selectSelectedFinding);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [findingDetails, setFindingDetails] = useState(initialFindingDetails);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...findingDetails };
    newDetails[name] = value;
    setFindingDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleCopyDescription = () => {
    const fakeEvent = { target: { name: 'description', value: description } };
    handleChange(fakeEvent);
  };

  const handleChangeChips = (value) => {
    const newDetails = { ...findingDetails };
    if (newDetails.usualFindings.includes(value)) {
      newDetails.usualFindings = newDetails.usualFindings.filter((x) => x !== value);
    } else {
      newDetails.usualFindings = [...newDetails.usualFindings, value];
    }
    setFindingDetails(newDetails);
  };

  const handleNewEvidence = () => {
    const newDetails = { ...findingDetails };
    const newEvidence = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    newDetails.evidence = [...newDetails.evidence, newEvidence];
    setFindingDetails(newDetails);
  };

  const handleUpdateDocument = (documentData) => {
    const newDetails = { ...findingDetails, evidence: [...findingDetails.evidence] };
    const index = newDetails.evidence.findIndex((x) => x.id === documentData.id);
    newDetails.evidence[index] = documentData;
    setFindingDetails(newDetails);
  };

  const handleDeleteDocument = (id) => {
    const newDetails = { ...findingDetails };
    newDetails.evidence = newDetails.evidence.filter((x) => x.id !== id);
    setFindingDetails(newDetails);
  };

  const handleInitialize = () => {
    if (finding.id) {
      setFindingDetails({ ...finding });
    } else {
      setFindingDetails({ ...initialFindingDetails });
    }
  };

  const handleSave = () => {
    dispatch(saveFinding(findingDetails));
    handleClose();
  };

  const handleCancel = () => {
    handleInitialize();
    handleClose();
  };

  const handleUnsavedChanges = (_, reason) => {
    if (unsavedChanges) {
      switch (reason) {
        case 'backdropClick':
          // Prevent outside click modal close
          break;
        default:
          setUnsavedChanges(false);
          handleCancel();
          break;
      }
    } else {
      handleInitialize();
      handleClose();
    }
  };

  useEffect(() => {
    handleInitialize();
  }, [finding]);

  return (
    <Modal open onClose={handleUnsavedChanges}>
      <Container variant="modal" disableGutters sx={{ width: '900px' }}>
        <Stack variant="modalTop" spacing={2}>
          <Typography variant="h6">
            {finding.id ? t('finding.modal.editFinding') : t('finding.modal.newFinding')}
          </Typography>
          <Stack>
            <Typography>{t('finding.modal.control')}</Typography>
            <Typography variant="body2">{controlHeader}</Typography>
          </Stack>
          <TextField
            variant="outlined"
            subtype="signinField"
            onChange={handleChange}
            id="finding-title-modal"
            name="title"
            value={findingDetails.title}
            InputLabelProps={{
              shrink: true,
            }}
            label={t('finding.modal.title')}
            required
          />
          <Stack>
            <Typography>{t('finding.criticality.title')}</Typography>
            <RadioGroup row name="criticality" value={findingDetails.criticality} onChange={handleChange}>
              {Criticality.list().map((type) => (
                <FormControlLabel
                  key={type}
                  value={type}
                  control={<Radio id={`criticality-${type}-modal`} variant="radio" />}
                  label={t(`finding.criticality.${type}`)}
                />
              ))}
            </RadioGroup>
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="end">
              <Button variant="outlined" size="small" onClick={handleCopyDescription} endIcon={<ContentCopyIcon />}>
                <Typography variant="buttonSmall">{t('finding.modal.copyNotesButton')}</Typography>
              </Button>
            </Stack>
            <TextField
              variant="outlined"
              subtype="signinField"
              onChange={handleChange}
              id="finding-description-modal"
              name="description"
              multiline
              minRows="4"
              value={findingDetails.description}
              InputLabelProps={{
                shrink: true,
              }}
              label={t('finding.modal.descriptionPlaceholder')}
              required
            />
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {UsualFindings.list().map((item) => (
              <Chip
                id={`usual-${item}`}
                key={item}
                variant={findingDetails.usualFindings?.includes(item) ? 'filled' : 'outlined'}
                subtype="evalChip"
                label={t(`finding.usualFinding.${item}`)}
                clickable
                onClick={() => handleChangeChips(item)}
              />
            ))}
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography>{t('control.evaluation.evidenceLink')}</Typography>
              <Button variant="outlined" onClick={handleNewEvidence} endIcon={<AddIcon />}>
                {t('control.evaluation.evidenceAdd')}
              </Button>
              {findingDetails.evidence.map((link) => (
                <DocumentLink
                  key={link.documentName}
                  document={link}
                  handleDeleteDocument={handleDeleteDocument}
                  handleUpdateDocument={handleUpdateDocument}
                />
              ))}
            </Stack>
          </Stack>
          <TextField
            variant="outlined"
            subtype="signinField"
            onChange={handleChange}
            id="finding-recommendation-modal"
            name="recommendation"
            multiline
            minRows="4"
            value={findingDetails.recommendation}
            InputLabelProps={{
              shrink: true,
            }}
            label={t('finding.modal.recommendationPlaceholder')}
          />
        </Stack>
        <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
          <Button id='cancel' variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
            <Typography variant="button">{t('finding.modal.cancel')}</Typography>
          </Button>
          <Button
            id='save'
            variant="contained"
            disabled={!validate(findingDetails)}
            startIcon={finding.id ? <SaveIcon /> : <AddIcon />}
            onClick={() => handleSave()}
          >
            <Typography variant="button">
              {finding.id ? t('finding.modal.saveChanges') : t('finding.modal.saveNew')}
            </Typography>
          </Button>
        </Stack>
      </Container>
    </Modal>
  );
}

export default FindingModal;
